import { Children, cloneElement, isValidElement, useState } from 'react';

import type { PropsWithChildren } from 'react';
import type { OptionValue, SelectValue } from './SelectProvider';
import type { SelectOptionProps } from './components/SelectOption';

import { SelectProvider } from './SelectProvider';
import { SelectButton } from './components/SelectButton';
import { SelectContainer } from './components/SelectContainer';
import { SelectOptionBox } from './components/SelectOptionBox';
import { SelectOption } from './components/SelectOption';

import { usePositionedFloaters } from 'components/hooks/usePositionedFloaters';

export interface SelectListboxProps {
  /** The possible options to select. */
  options: OptionValue[];
  /** The selected value by default. */
  defaultValue?: SelectValue;
  /** The max height of the options. */
  maxOptionHeight?: `${number}${'px' | 'rem'}`;
  /** `true` to disable the select box; otherwise, `false`. The default is `false`. */
  disabled?: boolean;
  /** The name of the property in the options structure that contains the label of the options. */
  buttonLabelProp?: string;
  /** `true` to render the labels in bold; otherwise, `false`. The default is `false`. */
  buttonLabelBold?: boolean;
  /** The variant of the select box. */
  variant?: 'noborder' | 'pill' | 'pillLeft' | 'pillRight' | 'darkBorder' | 'roundedLeft' | 'roundedRight';
  /** If true sets the option dropdown min-width to the width of the select button. */
  fullWidth?: boolean;
  /** The size of the select box. */
  size?: 'small' | 'medium' | 'default';
  /** `true` to align the right side of the options with the select box; otherwise, `false` to align on the left side. */
  optionPositionRight?: boolean;
  /** The breakpoint at which to switch to the mobile view. */
  mobileBreakpoint?: `${number}${'px' | 'rem'}`;
  /** The callback function that is invoked when an option is selected. */
  onChange?: (option: OptionValue) => void;
  /** Option to add a devider after a given amout of options in the select option box. */
  dividerAfter?: number;
  /** `true` to color border red to show not valid; otherwise, `false`. */
  notValid?: boolean;
}

/** Represents a component to render a select list box. */
export const SelectListbox = ({
  options,
  variant,
  fullWidth = false,
  size = 'default',
  optionPositionRight,
  defaultValue = -1,
  maxOptionHeight,
  disabled,
  buttonLabelProp = 'label',
  buttonLabelBold,
  onChange,
  dividerAfter,
  notValid = false,
  children,
}: PropsWithChildren<SelectListboxProps>) => {
  const [positionElement, setPositionElement] = useState<HTMLElement | null>(null);
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const { style: positionStyle } = usePositionedFloaters(positionElement, referenceElement, {
    mouseEvent: 'click',
    position: 'bottomLeft',
    offset: 4,
    maxFixedHeight: maxOptionHeight,
    horizontalPosition: optionPositionRight ? 'right' : 'left',
    mobileBreakpoint: '28rem',
    fullWidth,
  });

  return (
    <SelectProvider options={options} defaultValue={defaultValue}>
      <SelectContainer>
        <SelectButton
          disabled={disabled}
          buttonLabelProp={buttonLabelProp}
          buttonLabelBold={buttonLabelBold}
          variant={variant}
          size={size}
          notValid={notValid}
          onChange={onChange}
          ref={setReferenceElement}
        />

        <SelectOptionBox ref={setPositionElement} {...positionStyle}>
          {Children.map(children, (child, index) => {
            if (isValidElement<SelectOptionProps>(child)) {
              return cloneElement(child, {
                dividerAfter,
                key: index,
              });
            }
            return child;
          })}
        </SelectOptionBox>
      </SelectContainer>
    </SelectProvider>
  );
};

SelectListbox.SelectOption = SelectOption;
